import type { DomainTypeEnum } from "./domain";
import type { ImportExportStatuses } from "./encounterEntity";
import type { FacilityServiceFieldSetting } from "./facilitySettings";
import type { Sex } from "./groupingEntity";

// server side information about HCCs
export interface ServiceResultCodeHCC {
  ViewId?: string | null;
  Value?: string | null;
  Description?: string | null;
  Version?: string | null;
}

// service side information about code
export interface ServiceResultCodeInfo {
  ViewId?: string | null;
  CodeType?: string | null;
  CodeDescription?: string | null;
  HCCs: ServiceResultCodeHCC[];
  ExemptPOA?: boolean;
}

// service side information about edit parameter
export interface ServiceResultValidationEditParameter {
  ViewId?: string | null;
  Name?: string | null;
  Value?: string | null;
  ReferencedViewId?: string | null;
  Field?: string | null;
  CodeType?: string | null;
}

export interface ServiceResultValidationEditParameterWithRef extends ServiceResultValidationEditParameter {
  ReferencedViewId: string;
}

// service side information about validation edit
export interface ServiceResultValidationEdit {
  ViewId?: string | null;
  Id?: string | null;
  Description?: string | null;
  Details?: string | null;
  EditType?: string | null;
  Level?: string | null;
  Parameters: ServiceResultValidationEditParameter[];
  SourceData?: string | null;
  AutomationSource?: string | null;
  EncounterViewId?: string | null;
}

// service side information about validation edit
export interface ServiceResultMneEdit {
  ViewId: string | null;
  ReferencedViewId?: string | null;
  Code: string | null;
  CodeType: string | null;
  Date?: string | null;
  Policies?: ServiceResultMnePolicy[];
  // legacy TEE logic for edit intro text setting
  intro?: string | undefined;
}

export interface ServiceResultMnePolicy {
  ViewId: string | null;
  PolicyType: string | null;
  SeverityStatus: string | null;
  PolicyId: string | null;
  PolicyTitle: string | null;
  ArticleId: string | null;
  FiNumber: string | null;
  FiName: string | null;
  Mac: string | null;
  MacName: string | null;
  Notes: string | null;
  Sex: string | null;
  MinAgeInYears: string | null;
  Frequency?: string | null;
  FrequencySpan?: string | null;
  FrequencyUnit?: string | null;
  InformationalArticle?: string | null;
  DiagnosisGroups?: ServiceResultMneDiagnosisGroup[];
}

export interface ServiceResultMneDiagnosisGroup {
  ViewId: string | null;
  Status: string | null;
  IsAllCodes: boolean;
  IsCodeListTruncated: boolean;
  Codes: ServiceREsultMneDiagnosisGroupCode[];
}

export interface ServiceREsultMneDiagnosisGroupCode {
  ViewId: string | null;
  StartCode: string;
  EndCode: string;
}

export interface ServiceEncounterProcessPFSResult {
  ViewId: string | null;
  Key: string;
  SessionId: string;
  TotalFeeAmount?: number;
  TotalFacilityFeeAmount: number;
  TotalNonFacilityFeeAmount: number;
  Edits: ServiceResultPfsEdit[];
  ProcedureResults: ServiceResultPfsProcedureResult[];
  ProceduresOrder: string[];
}

export type ServiceResultPfsEditParameter = ServiceResultValidationEditParameter;

export interface ServiceResultPfsEdit {
  ViewId: string;
  Id: string;
  Description: string;
  Details: null;
  EditType: string;
  Level: string;
  SourceData: null;
  AutomationSource: null;
  Parameters: ServiceResultPfsEditParameter[];
}

export interface ServiceResultPfsProcedureResult {
  ViewId: string | null;
  ResequencePosition: number | null;
  Values: PfsProcedureResultValues;
}

export interface PfsProcedureResultValues {
  Date: string;
  StatusCode: string;
  WorkRVU: string;
  NonFacilityRVU: string;
  FacilityRVU: string;
  MpRVU: string;
  TotalNonFacilityRVU: string;
  TotalFacilityRVU: string;
  PCTCIndicator: string;
  GlobalSurgery: string;
  MultipleSurgeryIndicator: string;
  BilateralSurgery: string;
  AssistantAtSurgery: string;
  CoSurgeons: string;
  TeamSurgery: string;
  IntraoperativePercentage: string;
  DiagnosticImagingFamily: string;
  EndoscopicBaseCode: string;
  NonFacilityFeeAmount: string;
  FacilityFeeAmount: string;
  OPPSNonFacilityFeeAmount: string;
  OPPSFacilityFeeAmount: string;
  OPPSIndicator: string;
  NonInstitutionalTherapyReductionAmount: string;
  InstitutionalTherapyReductionAmount: string;
  ByReport: boolean;
  CarrierTotalFacilityRVU?: string;
  ConversionFactorType?: string;
  FeeAmount?: string;
  CarrierTotalRVU?: string;
}

export interface ProvidersImportResult {
  Added: number;
  Updated: number;
  Skipped: number;
  Deactivated: number;
  Errors: {
    Message: string;
    Count: number;
  }[];
}

export interface ProvidersImportJob {
  JobId: string;
  Status: ImportExportStatuses;
  Error: string | null;
  Result: ProvidersImportResult;
}

export interface ProvidersImportState {
  busy: boolean;
  facilityId: string | null;
  taskId: string | null;
  status: ImportExportStatuses | null;
  error: string | null;
  result: ProvidersImportResult | null;
}

// service side information about flags
export interface ServiceResultFlags {
  ViewId?: string | null;
  Flags?: string | null;
}

export interface ProcGroupingResultValues {
  // backend fields
  Payment?: string | number | null;
  PricingStatus?: string | number | null;
  Weight?: string | number | null;
  Percentage?: string | number | null;
  MedicareAmount?: string | number | null;
  AdjustedCoin?: string | number | null;
  PaymentIndicator?: string | null;
  StatusIndicator?: string | null;
  CompAdjFlag?: string | null;
  OutlierAmt?: string | number | null;
  FinalEAPG?: string | null;
  FinalEAPGCategory?: string | null;
  FinalEAPGType?: string | null;
  FinalEAPGDescription?: string | null;

  // UI fields separated on groups
  APC?: string | number | null;
  APCPayment?: string | number | null;
  APCDescription?: string | null;
  APCWeight?: string | number | null;
  APCPercentage?: string | number | null;
  APCMedicareAmount?: string | number | null;
  APCAdjustedCoin?: string | number | null;
  APCStatusIndicator?: string | null;
  APCCompAdjFlag?: string | null;
  APCOutlierAmt?: string | number | null;

  EAPG?: string | null;
  EAPGCategory?: string | null;
  EAPGType?: string | null;
  EAPGPayment?: string | number | null;
  EAPGDescription?: string | null;
  EAPGWeight?: string | number | null;
  EAPGPercentage?: string | number | null;
  EAPGPricingStatus?: string | number | null;

  ASCPayment?: string | number | null;
  ASCWeight?: string | number | null;
  ASCPercentage?: string | number | null;
  ASCMedicareAmount?: string | number | null;
  ASCAdjustedCoin?: string | number | null;
  ASCPaymentIndicator?: string | null;
}

export interface ProcGroupingResult {
  ViewId?: string | null;

  Values: ProcGroupingResultValues;
}

export interface FooterField {
  Name: string;
  Description: string;
  Place: string;
  FieldType: string;
}

// all this values are provided from the services. So this interface is used only to know possible values
export interface ServiceResultGroupingValues {
  Drg?: string | null;
  DrgType?: string | null;
  DrgDescription?: string | null;
  Mdc?: string | null;
  MdcDescription?: string | null;
  SeverityOfIllness: string | null;
  RiskOfMortality: string | null;
  DrgWeight?: string | null;
  DrgReimbursement?: string | number | null;
  CapitalPayment?: string | number | null;
  DrgOutlierThreshold?: string | number | null;
  TransplantPayment?: string | number | null;
  LongStay?: string | number | null;
  ShortStay?: string | number | null;
  Amlos?: string | number | null;
  CostOutlier?: boolean | null;
  Gmlos?: string | number | null;
  WageIndex?: string | number | null;
  McoHmoAmt?: string | number | null;
  TotalOperAmt?: string | number | null;
  TotalCapitalAmt?: string | number | null;
  PassThruAmt?: string | number | null;
  LowVolumeAmt?: string | number | null;
  ClaimPaymentId?: string | number | null;
  OutlierPayment?: string | number | null;
  FacilitySpecificPayment?: string | number | null;
  DRGAdjustedPayment?: string | number | null;
  ShortStayBlendedPayment?: string | number | null;
  TeachingPortion?: string | number | null;
  ECTPayment?: string | number | null;
  ComorbidityAdjustmentFactor?: string | number | null;
  TotalComorbidityFactor?: string | number | null;
  AgeAdjustmentFactor?: string | number | null;
  PerDiemRate?: string | null;
  TotalReimbursement?: string | number | null;
  TotalMedicareAmount?: string | number | null;
  TotalAdjustedCoin?: string | number | null;
  TotalOutlierAmt?: string | number | null;

  TotalWeight?: string | number | null;
  AdjustedEapgWeight?: string | number | null;
  AdjustedWeight?: string | number | null;

  LocationCBSA?: string | null;
  ServiceProvider?: string | null;
  CostToChargeRatio?: string | null;

  TotalFacilityFeeAmount?: number;
  TotalNonFacilityFeeAmount?: number;
}

// DELETE
// service side information about InpatientGrouping and OutpatientGrouping
export interface ServiceResultPatientGroupingResult {
  ViewId?: string | null;
  Description?: string | null;
  Edits: ServiceResultValidationEdit[];
  GrouperType?: string | null;
  GrouperVersion?: string | null;
  PricerType?: string | null;
  PricerDescription?: string | null;
  IsGPCSBased?: boolean | null;
  // use it to separate groupings
  IsInpatient?: boolean;
  Outdated?: boolean;
  Primary?: boolean | null;

  // all footer values
  Values?: ServiceResultGroupingValues;

  UseHac?: boolean | null;

  // inpatient grouping
  Flags: ServiceResultFlags[];

  // outpatient grouping
  ProcGroupingResults?: ProcGroupingResult[];

  DiagGroupingResults: DiagnosisResult[];
}

// service side information about InpatientGrouping and OutpatientGrouping common fields
export interface ServiceResultGroupingSourceResult {
  ViewId?: string | null;
  GrouperDescription?: string | null;
  GrouperTypeId?: string | number | null; // not used
  GrouperType?: string | null;
  GrouperVersion?: string | null;
  IsInpatient?: boolean; // use it to separate groupings
  PricerType?: string | null;
  PricerDescription?: string | null;
  PricerTypeId?: string | number | null; // not used
  Primary?: boolean | null;
  SimplePricing?: boolean | null; // not used
  UseAdmitApr?: boolean | null; // not used
  UseHac?: boolean | null;
  IsGPCSBased?: boolean | null;
  Description?: string | null; // show it as grouping description
}

// service side information about InpatientGrouping and OutpatientGrouping processing results
export interface ServiceResultGroupingResultResult {
  ViewId?: string | null;
  // current Key to keep processing sequences
  Key?: string | null;
  // description
  Description?: string;
  // all footer values
  Values?: ServiceResultGroupingValues;
  // inpatient grouping
  Flags: ServiceResultFlags[];
  // outpatient grouping
  ProcGroupingResults?: ProcGroupingResult[];

  DiagGroupingResults: DiagnosisResult[];

  // grouping Edits and TruCode Edits
  Edits: ServiceResultValidationEdit[];

  // proposed codes order
  DiagnosesOrder?: string[];
  InProceduresOrder?: string[];
  OutProceduresOrder?: string[];
}

export const EncounterCategory = {
  Inpatient: "Inpatient",
  Outpatient: "Outpatient",
  Professional: "Professional",
} as const;

export type EncounterCategoryEnum = (typeof EncounterCategory)[keyof typeof EncounterCategory];

// server side Encounter Change method Result
export interface ServiceEncounterValidationResult {
  IsInpatient: boolean;
  PrimaryCoder?: string;
  SecondaryCoder?: string;
  BaseDate?: string | null;
  ValidationEdits: ServiceResultValidationEdit[];
  FieldsEdits: ServiceResultValidationEdit[];
  CalculatedAge?: string | null;
  CodeInfos: ServiceResultCodeInfo[];
  MinEditLevelToSave?: string;
  LengthOfStay?: string | null;

  GroupingResultsKey?: string | null;
  MNEResultKey?: string | null;
  PFSResultKey?: string | null;
  RecordCreationDate: string | null;
  RecordFinalizedDate: string | null;
  RecordModifiedDate: string | null;
  SpentByPrimaryCoderTime: number | null;
  SpentBySecondaryCoderTime: number | null;
  GroupingSources?: ServiceResultGroupingSourceResult[];
  FacilityVersion?: string; // can be used to check if a facility configuration has changed
  Category: EncounterCategoryEnum;
}

// server side Encounter ProcessMNE method Result
export interface ServiceEncounterProcessMNEResult {
  Key?: string | null;
  MneEdits: ServiceResultMneEdit[];
}

// editable code fields
export interface ServiceViewCode {
  Code: string | null;
  CodeDescription: string | null;
  CustomCodeDescription: string | null;
  CodeType: string | null;
  Episode?: string | null;
  Modifiers?: string[] | null;
  Charges?: string | null;
  NonCoveredCharges?: string | null;
  UnitsOfService?: string | null;
  ProcedureDate?: string | Date | null;
  DiagnosesFlags?: string | null;
  PresentOnAdmission: string | null;
  ViewId: string | null;
  Amount?: string | null;
  RevenueCode?: string | null;
  Provider?: string | null;
  IsChargeDriven?: boolean;
  DxLinks?: string[] | null;
  PlaceOfService?: string | null;
}

// server side encounter representation
export interface ServiceEncounterView {
  ViewId?: string | null;
  IsInterfaced?: boolean;
  InterfaceCallbackUrl?: string;
  InterfaceMode?: string;
  IsDirty?: boolean | null;

  EncounterType: string;
  Facility: string;
  MedicalRecordNumber?: string | null;

  LastName?: string | null;
  FirstName?: string | null;
  MiddleInitial?: string | null;
  DateOfBirth?: string | null;
  PatientAge?: string | null;
  BirthWeight?: string | null;
  Service?: string | null;
  FinancialClass?: string | null;
  AccountNumber?: string | null;
  AdmitDate?: string | null;
  DischargeDate?: string | null;
  PatientStatus?: string | null;
  PatientSex?: string | null;
  BillType?: string | null;

  OverrideLOS?: string | null;
  TotalCharges?: string | null;
  ThirdPartyLiability?: string | null;

  RecordStatus?: string | null;
  SourceOfAdmission?: string | null;
  PayerFlag?: string | null;
  PlaceOfService?: string | null;
  ServiceLocation?: string | null;
  Provider?: string | null;

  AdmitDiagnosis: ServiceViewCode | null;
  Diagnoses: ServiceViewCode[];
  InProcedures: ServiceViewCode[];
  OutProcedures: ServiceViewCode[];
  VisitReasons: ServiceViewCode[];
  ProProcedures?: ServiceViewCode[];

  ValueCodes: ServiceViewCode[];
  ConditionCodes: ServiceViewCode[];
  BillingNotes: string[];
  StoredEdits?: ServiceResultValidationEdit[];

  // Supplementary data
  PrimaryCoder: string | null;
  SecondaryCoder: string | null;
  Reviewer: string | null;
  ReviewDate: string | null;
  Memo: string | null;
  CreatedOn: string | null;
  FinalizedOn: string | null;
  ModifiedOn: string | null;
  TotalCodingTime: number | null;
  CustomField1: string | null;
  CustomField2: string | null;
  CustomField3: string | null;
  CustomField4: string | null;
}

interface ServiceEncounterEntityChoiceListItem {
  ViewId?: string;
  Id?: string;
  Description?: string;
  Service?: string;
  NPI?: string;
  IsActive?: boolean;
}

interface ServiceEncounterEntityChoiceListItems {
  EncounterType?: ServiceEncounterEntityChoiceListItem;
  PatientStatus?: ServiceEncounterEntityChoiceListItem;
  Service?: ServiceEncounterEntityChoiceListItem;
  FinancialClass?: ServiceEncounterEntityChoiceListItem;
  RecordStatus?: ServiceEncounterEntityChoiceListItem;
  Sex?: ServiceEncounterEntityChoiceListItem;
  SourceOfAdmission?: ServiceEncounterEntityChoiceListItem;
  EncounterProvider?: ServiceEncounterEntityChoiceListItem;
  Providers?: ServiceEncounterEntityChoiceListItem[];

  FieldSettings?: FacilityServiceFieldSetting[];
}

interface ServiceEncounterEntityFacility {
  ViewId?: string;
  Id?: string;
  Domain?: DomainTypeEnum;
  Description?: string;
  CurrentUserPermissions?: string;
  AllowProfessionalCodingForCurrentUser?: boolean;
}

// result from the Create/Open/Get service
export interface ServiceEncounterEntity {
  ChoiceListItems?: ServiceEncounterEntityChoiceListItems;
  ConcurrencyVersion?: string;
  DocumentId: string;
  Encounter: ServiceEncounterView;
  Facility?: ServiceEncounterEntityFacility;
  GroupingResults: ServiceResultGroupingResultResult[] | null;
  IsDirty: boolean | undefined;
  LockedBy: string | null;
  ProcessMNEResult: ServiceEncounterProcessMNEResult | null;
  ProcessPFSResult: ServiceEncounterProcessPFSResult | null;
  ReadOnly?: boolean;
  ValidationResult: ServiceEncounterValidationResult | null;
}

// result from the Save service
export interface EncounterSaveServiceResult {
  ConcurrencyVersion?: string;
  length?: number;
}

// Processing result
export interface EncounterProcessResult {
  // helps to define item to merge result with
  type: "ValidationResult" | "GroupingResult" | "ProcessMNEResult" | "ProcessPFSResult";
  // ViewId, only for GroupingResult (they are stored in array)
  ViewId?: string | null;
  // services result
  processResult: EncounterSaveServiceResult;
}

// encounter entity from the Search service
export interface EncounterSearchServiceResultEntity {
  DocumentId: string;
  AccountNumber: string;
  EncounterType: string;
  Facility: string;
  IsLocking: boolean;

  EncounterTypeName?: string;
  FacilityName?: string;

  MedicalRecordNumber?: string;
  AdmitDate?: string;
  DischargeDate?: string;
  FirstName?: string;
  LastName?: string;
  MiddleInitial?: string;
  Service?: string;
  ServiceName?: string;
  Sex?: Sex;
  RecordStatus?: string;
  DateOfBirth?: string;
  ReviewDate?: string;
}

// result from the Search service
export interface EncountersSearchResult {
  value: EncounterSearchServiceResultEntity[];
}

export interface JSONPATCH {
  op: string;
  path: string;
  from?: string;
  value?: string | Record<string, unknown>;
}

export interface CodeValidation {
  Description: string;
  Field: string;
  Level?: string | null;
}
export interface DiagnosisResult {
  ViewId?: string | null;
  Values: {
    PsychComorbidityAdjustmentFactor?: string | null;
    PsychComorbidityCategoryDescription?: string | null;
  };
}
